/**
 * Warm-up world · "Juno's desk": a wooden desk seen from above, a ruler as the trail, pencils,
 * erasers, crayons and a paint palette scattered around (ART-BIBLE §5.3).
 */
import { rolePalette } from '@sketchling/core';
import type { Layer } from '../../Figure';
import { ellipse, rotate } from '../../geom';
import { rect, rng, rrect, spread } from '../kit';
import type { WorldArt } from '../types';

const palette = rolePalette({
  desk: '#C98E5E',
  deskDark: '#B57B4D',
  grain: '#A86F45',
  sheet: '#FFF8EC',
  pencilBody: '#F2C14E',
  pencilBlue: '#4C86C9',
  pencilRed: '#E5675A',
  pencilGreen: '#6FAA62',
  wood: '#F1D2A8',
  lead: '#4A3226',
  eraserPink: '#EE8C8C',
  sleeve: '#D8CDBD',
  palette: '#F3DDBA',
  paintBlue: '#4C86C9',
  paintYellow: '#F2C14E',
  paintRed: '#E5675A',
  paintGreen: '#6FAA62',
});

/** A pencil lying at (x, y), length l, angle deg. */
function pencil(x: number, y: number, l: number, deg: number, body: string, s: number): Layer[] {
  const t = 7 * s;
  const R = (d: string) => rotate(d, deg, x, y);
  return [
    { k: 'region', d: R(rrect(x - l / 2, y - t, l * 0.78, t * 2, 2 * s)), fill: body, shade: 3 * s },
    { k: 'region', d: R(`M${x + l * 0.28} ${y - t}L${x + l / 2} ${y}L${x + l * 0.28} ${y + t}Z`), fill: 'wood', shade: false },
    { k: 'region', d: R(`M${x + l * 0.43} ${y - t * 0.32}L${x + l / 2} ${y}L${x + l * 0.43} ${y + t * 0.32}Z`), fill: 'lead', shade: false, line: false },
    { k: 'region', d: R(rrect(x - l / 2 - 10 * s, y - t, 12 * s, t * 2, 3 * s)), fill: 'eraserPink', shade: false },
    { k: 'region', d: R(rect(x - l / 2, y - t, 6 * s, t * 2)), fill: 'sleeve', shade: false },
  ];
}

export const INTRO_ART: WorldArt = {
  id: 'intro',
  palette,
  theme: {
    sky: '#D8A273',
    skyLow: '#FFF1DA',
    ink: '#3F2A1E',
    label: '#FFF8EC',
    accents: ['#F2C14E', '#EE8C8C', '#4C86C9'],
    trail: { kind: 'ruler', color: '#F2C14E', edge: '#C99A2E', deco: '#8A5A2A' },
    card: { bg: '#F6E7CF', border: '#E8D2AE', shadow: '#DCC39C' },
    node: { ring: '#E9783A', done: '#4CC38A', disc: '#FFF8EC', locked: '#E3D3BC' },
    // Painted desk (public/worlds/intro*.webp): the paper strip runs through the middle (≈ 0.36–0.66).
    raster: { mid: 0.58, amp: 0.1, scrim: 'rgba(255,246,230,0.10)', portrait: { mid: 0.58, amp: 0.1 } },
  },
  scene: ({ w, h, zones }) => {
    const s = h / 300;
    const R = rng(3);
    const L: Layer[] = [];
    // planks
    const ph = 46 * s;
    for (let y = -ph, i = 0; y < h + ph; y += ph, i++) {
      L.push({ k: 'region', d: rect(-20, y, w + 40, ph), fill: i % 2 ? 'desk' : 'deskDark', shade: false, line: false });
      for (const x of spread(0, w, 160 * s, 40 + i)) L.push({ k: 'ink', d: `M${x} ${y + ph * (0.3 + R() * 0.4)}C${x + 30 * s} ${y + ph * 0.25} ${x + 70 * s} ${y + ph * 0.7} ${x + 120 * s} ${y + ph * 0.5}`, w: 0.45, color: 'grain' });
    }
    // sketchbook sheets under each level
    for (const [i, [x0, x1]] of zones.entries()) {
      const cx = (x0 + x1) / 2;
      L.push({ k: 'region', d: rotate(rrect(cx - (x1 - x0) * 0.42, h * 0.1, (x1 - x0) * 0.84, h * 0.8, 6 * s), i % 2 ? 1.5 : -1.2, cx, h / 2), fill: 'sheet', shade: 6 * s });
    }
    // props near the top and bottom edges
    const bodies = ['pencilBody', 'pencilBlue', 'pencilRed', 'pencilGreen'];
    spread(60 * s, w, 300 * s, 5).forEach((x, i) => {
      const top = i % 2 === 0;
      const kind = i % 3;
      const y = top ? h * 0.08 : h * 0.93;
      if (kind === 0) L.push(...pencil(x, y, 150 * s, top ? -8 : 10, bodies[i % 4]!, s));
      else if (kind === 1) {
        L.push({ k: 'region', d: rotate(rrect(x - 22 * s, y - 12 * s, 44 * s, 24 * s, 6 * s), top ? 12 : -14, x, y), fill: 'eraserPink', shade: 4 * s, rim: true });
        L.push({ k: 'region', d: rotate(rrect(x - 22 * s, y - 12 * s, 16 * s, 24 * s, 5 * s), top ? 12 : -14, x, y), fill: 'pencilBlue', shade: false });
      } else {
        L.push({ k: 'region', d: rotate(`M${x - 46 * s} ${y}C${x - 46 * s} ${y - 30 * s} ${x + 40 * s} ${y - 34 * s} ${x + 46 * s} ${y - 6 * s}C${x + 50 * s} ${y + 14 * s} ${x + 20 * s} ${y + 8 * s} ${x + 12 * s} ${y + 20 * s}C${x} ${y + 34 * s} ${x - 46 * s} ${y + 26 * s} ${x - 46 * s} ${y}Z`, top ? 10 : -6, x, y), fill: 'palette', shade: 6 * s });
        ['paintBlue', 'paintYellow', 'paintRed', 'paintGreen'].forEach((p, k) => L.push({ k: 'spot', d: ellipse(x - 28 * s + k * 16 * s, y - 8 * s + (k % 2) * 6 * s, 6 * s, 5 * s), fill: p, stage: 2 }));
      }
    });
    return { vb: [w, h], layers: L, guides: [] };
  },
};
